import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import BeltBadge from '../../components/common/BeltBadge';
import { getMyTrainerProfile } from '../../services/trainerService';
import { getClasses } from '../../services/classService';
import { getStudents } from '../../services/studentService';
import { getTrainerSummary } from '../../services/gamificationService';
import useAuth from '../../hooks/useAuth';
import { useTenant } from '../../context/TenantContext';
import { hasClasses } from '../../utils/publicProfile';
import useClubVocab from '../../hooks/useClubVocab';
import { TRIAL_ENABLED } from '../../config/features';
import {
  MdPeople, MdFitnessCenter, MdCheckCircle, MdSchedule, MdEmojiEvents,
  MdQrCode, MdReceiptLong, MdCampaign, MdPersonAdd, MdBarChart, MdStorefront,
} from 'react-icons/md';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const todayName = DAYS[new Date().getDay()];

const timeStr = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hr = parseInt(h);
  return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
};

function ProgressSummary({ summary: s }) {
  if (!s) return null;
  const hasHighlights = s.readyCount > 0 || s.streakStudents?.length > 0;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {s.readyCount > 0 && (
          <span className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 bg-green-50 text-green-700 rounded-full border border-green-100">
            🎯 {s.readyCount} progressing well
          </span>
        )}
        {s.streakStudents?.length > 0 && (
          <span className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 bg-orange-50 text-orange-700 rounded-full border border-orange-100">
            🔥 {s.streakStudents.length} on a strong streak
          </span>
        )}
        {!hasHighlights && (
          <p className="text-xs text-muted">No highlights yet — keep recording attendance.</p>
        )}
      </div>

      {s.streakStudents?.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-muted uppercase tracking-wide mb-1.5">
            Consistent attendance (7+ classes)
          </p>
          <div className="space-y-1.5">
            {s.streakStudents.map((st, i) => (
              <div key={i} className="flex items-center gap-2 bg-orange-50/50 rounded-xl px-3 py-2">
                <span className="text-sm">🔥</span>
                <p className="text-sm font-medium text-secondary flex-1 truncate">{st.name}</p>
                <span className="text-xs text-orange-600 font-semibold flex-shrink-0">{st.streak} in a row</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {s.recentActivity?.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-muted uppercase tracking-wide mb-1.5">Recent achievements</p>
          <div className="space-y-1.5">
            {s.recentActivity.slice(0, 3).map((a, i) => (
              <div key={i} className="flex items-center gap-2 bg-nav-hover rounded-xl px-3 py-2">
                <span className="text-base flex-shrink-0">{a.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-secondary truncate">{a.studentName}</p>
                  <p className="text-[10px] text-muted truncate">{a.name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TrainerDashboard() {
  document.title = 'Dashboard — TrainAxis';
  const { user } = useAuth();
  const { club } = useTenant();
  const v = useClubVocab();
  // Open-floor gym clubs have no classes/sessions/attendance — the trainer dashboard degrades
  // to members + announcements + a simple "no scheduled duties" note. Reversible, no data touched.
  const showClasses = hasClasses(club?.clubType);

  const { data: profileData } = useQuery({
    queryKey: ['trainer-me'],
    queryFn:  () => getMyTrainerProfile().then(r => r.data),
  });

  const profile = profileData?.profile;

  const { data: classData, isPending: loadingClasses } = useQuery({
    queryKey: ['classes'],
    queryFn:  () => getClasses({ isActive: 'true' }).then(r => r.data),
  });

  const { data: studentsData } = useQuery({
    queryKey: ['students', 1, '', '', ''],
    queryFn:  () => getStudents({ limit: 5 }).then(r => r.data),
  });

  const { data: progressData, isPending: loadingProgress } = useQuery({
    queryKey: ['trainer-progress-summary'],
    queryFn:  () => getTrainerSummary().then(r => r.data),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const allClasses = classData?.classes || [];
  const todayClasses = allClasses.filter(c => c.days?.includes(todayName));

  const STAT_CARDS = [
    {
      label: `Total Active ${v.members}`,
      value: studentsData?.total ?? '—',
      Icon:  MdPeople,
      bg:    'bg-blue-50', text: 'text-blue-600',
    },
    // Today's Classes count — hidden for open-floor gym clubs.
    ...(showClasses ? [{
      label: "Today's Classes",
      value: loadingClasses ? '…' : todayClasses.length,
      Icon:  MdFitnessCenter,
      bg:    'bg-green-50', text: 'text-green-600',
    }] : []),
    {
      label: 'My Specializations',
      value: profile?.specializations?.length ?? '—',
      Icon:  MdEmojiEvents,
      bg:    'bg-purple-50', text: 'text-purple-600',
    },
  ];

  return (
    <div>
      {/* Welcome */}
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-secondary">
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, {user?.name?.split(' ')[0] || 'Trainer'}
          </h1>
          {profile?.specializations?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {profile.specializations.map(s => (
                <span key={s} className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full border border-green-100">{s}</span>
              ))}
            </div>
          )}
        </div>
        {showClasses && (
          <div className="hidden sm:flex gap-2">
            <Link to="/trainer/attendance">
              <Button><MdQrCode className="text-lg" /> Attendance</Button>
            </Link>
            <Link to="/trainer/schedule">
              <Button variant="ghost"><MdSchedule /> Schedule</Button>
            </Link>
          </div>
        )}
      </div>

      {/* Mobile quick action grid */}
      <div className="grid grid-cols-2 sm:hidden gap-2.5 mb-5">
        {[
          ...(showClasses ? [
            { to: '/trainer/attendance',    Icon: MdQrCode,      label: 'Take Attendance', bg: 'bg-green-50',   color: 'text-green-600'  },
            { to: '/trainer/schedule',       Icon: MdSchedule,    label: "Today's Classes", bg: 'bg-blue-50',    color: 'text-blue-600'   },
          ] : []),
          { to: '/trainer/students',       Icon: MdPeople,      label: `My ${v.members}`,     bg: 'bg-primary-light', color: 'text-primary' },
          { to: '/trainer/expenses',       Icon: MdReceiptLong, label: 'Add Expense',     bg: 'bg-amber-50',   color: 'text-amber-600'  },
          ...(TRIAL_ENABLED ? [{ to: '/trainer/trial-students', Icon: MdPersonAdd, label: 'Website Requests', bg: 'bg-purple-50', color: 'text-purple-600' }] : []),
          { to: '/trainer/announcements',  Icon: MdCampaign,    label: 'Announcements',   bg: 'bg-cyan-50',    color: 'text-cyan-600'   },
        ].map(({ to, Icon, label, bg, color }) => (
          <Link
            key={to}
            to={to}
            className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl p-4 ${bg} active:opacity-75 transition-opacity`}
            style={{ minHeight: 72 }}
          >
            <Icon className={`text-2xl ${color}`} />
            <span className={`text-[11px] font-semibold text-center leading-tight ${color}`}>{label}</span>
          </Link>
        ))}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {STAT_CARDS.map(({ label, value, Icon, bg, text }) => (
          <Card key={label}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted text-xs font-medium">{label}</p>
                <p className="text-secondary font-bold text-2xl mt-1">{value}</p>
              </div>
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${bg}`}>
                <Icon className={`text-2xl ${text}`} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Open-floor gym: no scheduled duties — graceful empty state in place of class widgets */}
      {!showClasses && (
        <Card className="mb-4">
          <div className="flex items-center gap-3 py-2">
            <div className="w-11 h-11 rounded-2xl bg-nav-hover flex items-center justify-center flex-shrink-0">
              <MdStorefront className="text-2xl text-muted" />
            </div>
            <div>
              <p className="font-semibold text-secondary text-sm">No scheduled duties</p>
              <p className="text-xs text-muted mt-0.5">
                This club runs on an open floor — no classes or attendance to manage. Your members
                and announcements are below.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Today's classes */}
      {showClasses && (
      <Card className="mb-4">
        <h2 className="font-semibold text-secondary text-sm mb-3">Today's Classes — {todayName}</h2>
        {loadingClasses ? (
          <div className="h-20 bg-nav-hover animate-pulse rounded-xl" />
        ) : !todayClasses.length ? (
          <p className="text-muted text-sm py-4 text-center">No classes scheduled for today</p>
        ) : (
          <div className="space-y-2">
            {todayClasses.map(c => (
              <div key={c._id} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-nav-hover">
                <div className="min-w-0">
                  <p className="font-medium text-secondary text-sm truncate">{c.name}</p>
                  <p className="text-xs text-muted truncate">{c.discipline}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-primary">{timeStr(c.startTime)}</p>
                  {c.endTime && <p className="text-xs text-muted">until {timeStr(c.endTime)}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      )}

      {/* Student Progress card — attendance-driven, hidden for open-floor gym clubs */}
      {showClasses && (
      <Card className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-secondary text-sm flex items-center gap-1.5">
            <MdBarChart className="text-primary" /> {v.member} Progress
          </h2>
        </div>
        {loadingProgress ? (
          <div className="space-y-2">
            {[1,2].map(i => <div key={i} className="h-8 bg-nav-hover animate-pulse rounded-xl" />)}
          </div>
        ) : !progressData?.summary ? (
          <p className="text-muted text-sm text-center py-4">
            Student progress will appear after attendance is recorded.
          </p>
        ) : (
          <ProgressSummary summary={progressData?.summary} />
        )}
      </Card>
      )}

      {/* Recent students */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-secondary text-sm">Recent {v.members}</h2>
          <Link to="/trainer/students" className="text-primary text-xs font-medium hover:underline">
            View all →
          </Link>
        </div>
        {!studentsData?.students?.length ? (
          <p className="text-muted text-sm text-center py-6">No {v.members.toLowerCase()} yet</p>
        ) : (
          <div className="space-y-2">
            {studentsData.students.slice(0, 5).map(s => (
              <div key={s._id} className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary-light text-primary rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {(s.user?.name || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-secondary truncate">{s.user?.name}</p>
                  <p className="text-xs text-muted">{s.discipline}</p>
                </div>
                <BeltBadge belt={s.currentGrade} size="xs" />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export default TrainerDashboard;
